<section class="cellular-report-center">
  <header class="page-header">
    <div>
      <div class="eyebrow">Root administrator workspace</div>
      <h1>Billing workspace</h1>
      <p>Prepare monthly usage reports, review charges, and manage billing configuration.</p>
    </div>
    <div class="management-links">
      <a mat-stroked-button routerLink="/layout/report/cellular-reports/overlay-bundles">Manage Overlay bundles</a>
      <a mat-stroked-button routerLink="/layout/report/cellular-reports/pricing">Data Overages &amp; Active Partners</a>
    </div>
  </header>
  <div class="review-banner" *ngIf="periodStatus === 'COMPLETED'" role="status"><mat-icon>lock</mat-icon><div><strong>Billing complete for {{ months[month - 1] }} {{ year }}</strong><span>Earlier versions are read-only history. New reports, review submissions and billing submissions are disabled.</span><a [routerLink]="['/layout/report/cellular-reports/review', finalReportId]">Open the final billing document</a></div></div>
  <div class="history-error" *ngIf="periodStatus === 'CONFLICT'" role="alert">Multiple historical completions exist for this month. Billing actions are blocked pending administrator reconciliation.</div>

  <div class="review-banner" *ngIf="reviewReportId">
    <mat-icon>rate_review</mat-icon>
    <div>
      <strong>Review link opened</strong>
      <span>The saved report is displayed below.</span>
      <a [routerLink]="['/layout/report/cellular-reports/review', reviewReportId]">Open approval and revision review</a>
    </div>
  </div>

  <mat-card class="workflow-card">
    <mat-horizontal-stepper [linear]="false"
                            [selectedIndex]="selectedStepIndex"
                            (selectionChange)="selectedStepIndex = $event.selectedIndex">
      <mat-step [completed]="periodForm.valid">
        <ng-template matStepLabel>Reporting period</ng-template>
        <div class="step-content">
          <div class="step-heading">
            <div class="step-number">1</div>
            <div>
              <h2>Select the billing month</h2>
              <p>Saved report versions are grouped by this reporting period.</p>
            </div>
          </div>

          <form [formGroup]="periodForm" class="period-form">
            <mat-form-field appearance="outline">
              <mat-label>Month</mat-label>
              <mat-select formControlName="month" (selectionChange)="onPeriodChanged()">
                <mat-option *ngFor="let monthName of months; let index = index" [value]="index + 1">
                  {{ monthName }}
                </mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Year</mat-label>
              <mat-select formControlName="year" (selectionChange)="onPeriodChanged()">
                <mat-option *ngFor="let option of years" [value]="option">{{ option }}</mat-option>
              </mat-select>
            </mat-form-field>
          </form>

          <section class="version-panel" aria-labelledby="saved-version-title">
            <div class="panel-heading">
              <div>
                <h3 id="saved-version-title">Saved versions</h3>
                <p>{{ months[month - 1] }} {{ year }} · Compare saved billing totals and review decisions.</p>
              </div>
              <mat-spinner *ngIf="historyBusy" diameter="22"></mat-spinner>
            </div>

            <div class="history-loading" *ngIf="historyBusy" role="status">Loading saved reports and review status…</div>
            <div class="history-error" *ngIf="!historyBusy && historyError" role="alert">
              <span>{{ historyError }}</span><button mat-stroked-button type="button" (click)="refreshVersions()">Retry</button>
            </div>
            <div class="version-table-scroll" tabindex="0" aria-label="Scrollable saved report versions" *ngIf="!historyBusy && !historyError && versions.length">
              <table class="version-table">
                <caption class="visually-hidden">Saved {{ months[month - 1] }} {{ year }} 4GLTE report versions. Usage and charges are in gigabytes.</caption>
                <thead><tr><th scope="col">Version / saved</th><th scope="col">Report rows</th><th scope="col">Verified usage</th><th scope="col">Chargeable GB</th><th scope="col">Review status</th><th scope="col">Actions</th></tr></thead>
                <tbody><tr *ngFor="let report of versions; let first = first">
                  <td data-label="Version / saved"><div class="version-title"><strong>Version {{ report.version }}</strong><span class="latest-version" *ngIf="report.workflowStage === 'COMPLETED'">Final billing document</span><span class="latest-version" *ngIf="first && periodStatus !== 'COMPLETED'">Latest</span></div><small>{{ report.createdAt | date:'MMM d, y, h:mm a' }}</small></td>
                  <td data-label="Report rows"><strong>{{ report.rowCount | number }} 4GLTE</strong><small *ngIf="report.overlayRowCount">{{ report.overlayRowCount | number }} Overlay rows</small><small>{{ report.billedRowCount === undefined ? '—' : (report.billedRowCount | number) }} marked Bill: Yes</small><button mat-button type="button" class="duplicate-link" *ngIf="report.duplicateMacRows" (click)="showDuplicateVersion(report)">{{ report.duplicateMacRows | number }} duplicate MAC rows · View</button></td>
                  <td data-label="Verified usage" class="numeric"><strong>{{ report.totalVerifiedUsageGb === undefined ? '—' : (report.totalVerifiedUsageGb | number:'1.2-2') }} GB</strong><small>{{ report.thresholdGb | number }} GB save threshold</small></td>
                  <td data-label="Chargeable GB" class="numeric"><strong class="charge-total">{{ report.totalChargeGb === undefined ? '—' : (report.totalChargeGb | number) }} GB</strong><small *ngIf="report.overlayRowCount">Overlay: {{ report.overlayTotalChargeGb | number:'1.0-6' }} GB</small><small>{{ report.discardedRowCount | number }} 4GLTE rows excluded at save</small></td>
                  <td data-label="Review status"><span class="version-status" [attr.data-status]="report.workflowStage || report.reviewStatus">{{ reviewStatusLabel(report) }}</span><small *ngIf="report.workflowRevision">Workflow revision {{ report.workflowRevision }}</small><small *ngIf="report.workflowUpdatedAt || report.reviewUpdatedAt">{{ (report.workflowUpdatedAt || report.reviewUpdatedAt) | date:'MMM d, h:mm a' }}</small><small class="revision-comment" *ngIf="report.reviewComment" [matTooltip]="report.reviewComment">{{ report.reviewComment }}</small></td>
                  <td data-label="Actions"><div class="version-actions">
                    <button mat-button type="button" [attr.aria-label]="'Open original snapshot version ' + report.version" (click)="openVersion(report)">{{ periodStatus === 'COMPLETED' ? 'Original snapshot' : 'Open' }}</button>
                    <a mat-stroked-button [attr.aria-label]="'View workflow for report version ' + report.version" [routerLink]="['/layout/report/cellular-reports/review', report._id]">{{ report.workflowStage === 'COMPLETED' ? 'View final' : report.workflowStage === 'SUPERSEDED' ? 'View history' : 'Review' }}</a>
                    <button mat-button type="button" [attr.aria-label]="'Export report version ' + report.version + ' as XLSX'" (click)="exportVersion(report, $event)"><mat-icon aria-hidden="true">download</mat-icon> XLSX</button>
                  </div></td>
                </tr></tbody>
              </table>
            </div>
            <ng-container *ngIf="!historyBusy && !historyError && !versions.length" [ngTemplateOutlet]="noVersions"></ng-container>
            <ng-template #noVersions>
              <div class="empty-versions">
                <mat-icon>history</mat-icon>
                <span>No saved versions for {{ months[month - 1] }} {{ year }}.</span>
              </div>
            </ng-template>
          </section>

          <details class="billing-help" *ngIf="savedDrafts.length"><summary>Resume a saved reconciliation draft · {{ savedDrafts.length }}</summary><div *ngFor="let draft of savedDrafts" class="draft-resume-row"><span>{{ draft.createdAt | date:'medium' }} · r{{ draft.revision }} · {{ draft.status === 'COMPLETE' ? 'Completed reconciliation' : 'In progress' }}</span><button mat-button type="button" [disabled]="reportBusy" (click)="resumeDraft(draft._id)">Resume draft</button></div></details>
          <div class="step-actions">
            <button mat-raised-button color="primary" type="button"
                    [disabled]="periodForm.invalid || !canCreateReport" (click)="selectedStepIndex = 1">
              Continue to uploads
              <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </div>
      </mat-step>

      <mat-step [completed]="!!currentReport || !!reconciliation">
        <ng-template matStepLabel>Upload source reports</ng-template>
        <div class="step-content">
          <div class="step-heading">
            <div class="step-number">2</div>
            <div>
              <h2>Upload all three monthly sources</h2>
              <p>Files are validated and reconciled in memory. Source files are not stored in this phase.</p>
            </div>
          </div>

          <div class="source-grid">
            <article class="source-card" [class.file-ready]="files.license">
              <div class="source-icon license"><mat-icon>description</mat-icon></div>
              <div class="source-copy">
                <span class="source-order">Required source 1</span>
                <h3>License Report</h3>
                <p>CSV or XLSX · up to 20 MB</p>
              </div>
              <input #licenseInput hidden type="file" accept=".csv,.xlsx"
                     (change)="onFileSelected('license', $event)">
              <div class="selected-file" *ngIf="files.license; else chooseLicense">
                <mat-icon>check_circle</mat-icon>
                <span><strong>{{ files.license.name }}</strong><small>{{ fileSize(files.license) }}</small></span>
                <button mat-icon-button type="button" aria-label="Remove License Report"
                        (click)="clearFile('license'); licenseInput.value = ''"><mat-icon>close</mat-icon></button>
              </div>
              <ng-template #chooseLicense>
                <button mat-stroked-button type="button" [disabled]="!canCreateReport" (click)="licenseInput.click()">
                  <mat-icon>upload_file</mat-icon> Choose file
                </button>
              </ng-template>
            </article>

            <article class="source-card" [class.file-ready]="files.teal">
              <div class="source-icon teal"><mat-icon>sim_card</mat-icon></div>
              <div class="source-copy">
                <span class="source-order">Required source 2</span>
                <h3>Teal Report</h3>
                <p>CSV · up to 20 MB</p>
              </div>
              <input #tealInput hidden type="file" accept=".csv" (change)="onFileSelected('teal', $event)">
              <div class="selected-file" *ngIf="files.teal; else chooseTeal">
                <mat-icon>check_circle</mat-icon>
                <span><strong>{{ files.teal.name }}</strong><small>{{ fileSize(files.teal) }}</small></span>
                <button mat-icon-button type="button" aria-label="Remove Teal Report"
                        (click)="clearFile('teal'); tealInput.value = ''"><mat-icon>close</mat-icon></button>
              </div>
              <ng-template #chooseTeal>
                <button mat-stroked-button type="button" [disabled]="!canCreateReport" (click)="tealInput.click()">
                  <mat-icon>upload_file</mat-icon> Choose file
                </button>
              </ng-template>
            </article>

            <article class="source-card" [class.file-ready]="files.kore">
              <div class="source-icon kore"><mat-icon>settings_input_antenna</mat-icon></div>
              <div class="source-copy">
                <span class="source-order">Required source 3</span>
                <h3>KORE Report</h3>
                <p>CSV · up to 20 MB</p>
              </div>
              <input #koreInput hidden type="file" accept=".csv" (change)="onFileSelected('kore', $event)">
              <div class="selected-file" *ngIf="files.kore; else chooseKore">
                <mat-icon>check_circle</mat-icon>
                <span><strong>{{ files.kore.name }}</strong><small>{{ fileSize(files.kore) }}</small></span>
                <button mat-icon-button type="button" aria-label="Remove KORE Report"
                        (click)="clearFile('kore'); koreInput.value = ''"><mat-icon>close</mat-icon></button>
              </div>
              <ng-template #chooseKore>
                <button mat-stroked-button type="button" [disabled]="!canCreateReport" (click)="koreInput.click()">
                  <mat-icon>upload_file</mat-icon> Choose file
                </button>
              </ng-template>
            </article>
          </div>

          <div class="file-error" *ngIf="fileError" role="alert">
            <mat-icon>error_outline</mat-icon>{{ fileError }}
          </div>

          <details class="field-reference">
            <summary>Expected fields in each source</summary>
            <div class="field-columns">
              <p><strong>License</strong><span>mac, name, msp_name, firmware_version, iccid, portal_overlay_aggregated_data, portal_4glte_aggregated_data</span></p>
              <p><strong>Teal</strong><span>eID, Bootstrap ICCID, Device Group Name, Device Name, Total Usage (Bytes), Plan Name</span></p>
              <p><strong>KORE</strong><span>SIM SID, Unique Name, ICCID, Total data (MB)</span></p>
            </div>
          </details>

          <div class="step-actions split-actions">
            <button mat-button type="button" (click)="selectedStepIndex = 0">
              <mat-icon>arrow_back</mat-icon> Back
            </button>
            <button mat-raised-button color="primary" type="button"
                    [disabled]="!canGenerate || previewBusy" (click)="generatePreview()">
              <mat-spinner *ngIf="previewBusy" diameter="20"></mat-spinner>
              <mat-icon *ngIf="!previewBusy">rule</mat-icon>
              {{ previewBusy ? 'Validating reports…' : 'Validate and build report' }}
            </button>
          </div>
        </div>
      </mat-step>

      <mat-step [completed]="!!currentReport || (!!reconciliation && reconciliation.status === 'COMPLETE')">
        <ng-template matStepLabel>Resolve duplicate MACs</ng-template>
        <app-duplicate-mac-reconciliation *ngIf="reconciliation" [draft]="reconciliation" [readOnly]="!canCreateReport || saveBusy"
          (changed)="acceptReconciliation($event)" (continued)="selectedStepIndex = 3"></app-duplicate-mac-reconciliation>
        <p *ngIf="!reconciliation" class="billing-help">{{ currentReport ? 'This is an existing saved report. Its original billing snapshot remains unchanged.' : 'Upload and validate the reports to start reconciliation.' }}</p>
      </mat-step>

      <mat-step [completed]="!!currentReport">
        <ng-template matStepLabel>Review and export</ng-template>
        <div *ngIf="!currentReport && (!reconciliation || reconciliation.status !== 'COMPLETE')" class="step-content"><h2>Complete source reconciliation first</h2><p>Duplicate usage must be resolved before the billing tables can be reviewed or exported. Drafts and investigation cases remain open.</p><button mat-stroked-button type="button" (click)="selectedStepIndex = reconciliation ? 2 : 1">{{ reconciliation ? 'Resolve duplicate MACs' : 'Upload source reports' }}</button></div>
        <div class="report-step" *ngIf="currentReport || (reconciliation && reconciliation.status === 'COMPLETE')">
          <div class="report-heading">
            <div>
              <span class="report-period">{{ months[month - 1] }} {{ year }}</span>
              <h2>Consolidated cellular usage</h2>
              <p *ngIf="!currentReport">Choose the verification source and billing decision for each row before saving.</p>
              <p *ngIf="currentReport">Viewing saved version {{ currentReport.version }} created {{ currentReport.createdAt | date:'medium' }}.</p>
            </div>
            <button mat-stroked-button type="button" [disabled]="!canCreateReport" (click)="selectedStepIndex = 1">
              <mat-icon>upload_file</mat-icon> Replace source files
            </button>
          </div>

          <div class="summary-grid" *ngIf="summary">
            <div><span>4GLTE rows</span><strong>{{ rows.length | number }}</strong></div>
            <div><span>License source</span><strong>{{ summary.licenseRows | number }}</strong></div>
            <div><span>Teal source</span><strong>{{ summary.tealRows | number }}</strong></div>
            <div><span>KORE source</span><strong>{{ summary.koreRows | number }}</strong></div>
            <div [class.warning-summary]="summary.duplicateMacRows"><span>Duplicate MAC rows</span><strong>{{ summary.duplicateMacRows | number }}</strong><button mat-button type="button" *ngIf="summary.duplicateMacRows" (click)="duplicatesOnly = true; reportTabs.selectedIndex = 0">View duplicate MACs</button></div>
          </div>
          <button *ngIf="reconciliation" mat-button type="button" (click)="selectedStepIndex = 2">View reconciliation decisions · {{ reconciliation.summary.resolved }} resolved</button>

          <mat-tab-group #reportTabs class="report-tabs" animationDuration="0ms" [selectedIndex]="reportTabIndex" (selectedIndexChange)="reportTabIndex = $event">
            <mat-tab label="4GLTE Usage">
              <app-billing-msp-groups kind="cellular" [rows]="rows"
                [readOnly]="saveBusy || !canCreateReport" [(duplicatesOnly)]="duplicatesOnly" (edited)="markUnsavedChanges()"></app-billing-msp-groups>
            </mat-tab>
            <mat-tab label="Pooling Data">
              <app-cellular-pooling [rows]="rows" [available]="!!pooling && pooling.available !== false"
                [snapshot]="pooling" (policiesChange)="pooling.version = 3; pooling.policies = $event; markUnsavedChanges()"
                [revision]="poolingRevision" [readOnly]="saveBusy || !canCreateReport"></app-cellular-pooling>
            </mat-tab>
            <mat-tab label="Overlay Usage">
              <p *ngIf="overlayError" class="file-error" role="alert">{{ overlayError }}. Correct the License source and upload it again to create the Overlay report.</p>
              <app-billing-msp-groups kind="overlay" [rows]="overlayRows" [pricingRevision]="pricingRevision" [readOnly]="saveBusy || !canCreateReport" [(duplicatesOnly)]="duplicatesOnly" (edited)="markUnsavedChanges()">
                <button mat-stroked-button type="button" [disabled]="!canCreateReport || !overlayRows.length" (click)="refreshOverlayMapping()">Refresh bundle mapping</button>
              </app-billing-msp-groups>
            </mat-tab>
            <mat-tab label="Overlay Bundles"><app-overlay-bundles [disabled]="saveBusy || !canCreateReport" (changed)="refreshOverlayMapping()"></app-overlay-bundles></mat-tab>
            <mat-tab label="Data Overages"><app-billing-pricing section="overages" [embedded]="true" [disabled]="saveBusy || !canCreateReport" (changed)="onPricingChanged()"></app-billing-pricing></mat-tab>
            <mat-tab label="Active Partners"><app-billing-pricing section="partners" [embedded]="true" [disabled]="saveBusy || !canCreateReport" (changed)="onPricingChanged()"></app-billing-pricing></mat-tab>
          </mat-tab-group>

          <div class="save-bar" *ngIf="rows.length">
            <div>
              <strong>Ready to create a report version?</strong>
              <span>Choose separate 4GLTE verified-usage and Overlay usage minimums when saving. Overlay eligibility remains above 14 GB.</span>
              <mat-checkbox *ngIf="overlayRows.length" [(ngModel)]="includeOverlay" [disabled]="saveBusy || !canCreateReport" (change)="markUnsavedChanges()">Include Overlay report ({{ overlayRows.length | number }} rows)</mat-checkbox>
            </div>
            <button mat-raised-button color="primary" type="button"
                    [disabled]="saveBusy || !canCreateReport" (click)="saveAndExport()">
              <mat-spinner *ngIf="saveBusy" diameter="20"></mat-spinner>
              <mat-icon *ngIf="!saveBusy">save_alt</mat-icon>
              {{ saveBusy ? 'Saving…' : 'Save and export' }}
            </button>
          </div>
        </div>
      </mat-step>
    </mat-horizontal-stepper>
  </mat-card>
</section>
